import reactUpdate from 'immutability-helper';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import {buildReactionFromComponents} from 'chemistry/ChemistryDAO';
import makeCancelable, {CancelablePromise} from 'lib/makeCancelable';
import {
  DiagramBuildRequest,
  getDiagramStatus,
  isTableEvaluated,
  knownVersionIds,
  makeDiagramBuildRequest,
  ReactionComponents,
  readDiagramComponents,
  readReactionRows,
  readSavedReactionIdentity,
  tableForBuiltDiagram,
  toReactionComponents,
  withRecordedVersions,
} from 'notebook/integrations/chemistry/reaction-scheme/reactionDiagramSync';
import {StoichiometryStructuredTable} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {NoteLinkedFile, FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';

type OnUpdate = (updatedFile: NoteLinkedFile, updatedTable: StoichiometryStructuredTable) => void;

export interface ReactionSchemeSyncState {
  /**
   * True when the diagram was already stale versus the table when the Entry opened, before any
   * change this session. This is the case that must not auto-save (BNCH-144118); it is surfaced as a
   * banner instead so the update is attributed to a deliberate user action.
   */
  isOutOfSync: boolean;
  /** True while a diagram build (automatic or manual) is in flight. */
  isSyncing: boolean;
  /** True while a user-initiated rebuild (`syncNow`) is in flight; used to lock the table against races. */
  isManuallySyncing: boolean;
  /** Brings the diagram up to date with the table. Backs the on-open "out of date" banner. */
  syncNow: () => void;
}

/**
 * Keeps the reaction scheme diagram in step with the stoichiometry table's reactants and products.
 *
 * Once the table has finished evaluating, the diagram is compared with the table (getDiagramStatus):
 * - In sync: nothing to do.
 * - Untracked (the table predates `syncedComponents`): built automatically once this session changes
 *   the reaction; otherwise nothing, since there's no record to say the diagram is stale.
 * - Stale, and this session changed the reaction (see useChangedThisSession): rebuild automatically.
 * - Stale, or missing the versions the table now knows, with no change this session: the diagram was
 *   already out of date when the Entry opened. Merely opening an Entry must never save it, or the
 *   opener would become the author of someone else's change (BNCH-144118). So if a diagram file
 *   exists, `isOutOfSync` shows a banner and `syncNow` brings it up to date on click.
 *
 * A rebuild calls the chemistry service and invokes `onUpdate` with the new NoteLinkedFile and the
 * table with its updated `syncedComponents`.
 *
 * @param structuredTable - The stoichiometry table containing reaction components
 * @param computedTable - The evaluated (computed) version of the table rows
 * @param reactionSchemeFile - The NoteLinkedFile that stores the reaction scheme drawing
 * @param onUpdate - Callback invoked with (updatedFile, updatedTable) when the diagram is updated
 * @param enabled - Whether syncing is allowed (false in locked/viewer contexts, which cannot save)
 */
export default function useSyncReactionComponentsFromTable(
  structuredTable: StoichiometryStructuredTable,
  computedTable: EvaluatedTable,
  reactionSchemeFile: NoteLinkedFile,
  onUpdate: OnUpdate,
  enabled: boolean
): ReactionSchemeSyncState {
  const diagramComponents = useMemo(
    () => readDiagramComponents(structuredTable.metadata.syncedComponents),
    [structuredTable.metadata.syncedComponents]
  );
  const rows = useMemo(
    () => readReactionRows(structuredTable.columns, structuredTable.rows, computedTable),
    [structuredTable.columns, structuredTable.rows, computedTable]
  );
  const tableComponents = useMemo(() => toReactionComponents(rows), [rows]);
  const isReady = isTableEvaluated(structuredTable, computedTable);

  const changedThisSession = useChangedThisSession(
    structuredTable,
    isReady,
    tableComponents,
    diagramComponents
  );
  // Held across renders where the table is re-evaluating, so the banner doesn't flicker.
  const status = useSettledValue(isReady, getDiagramStatus(tableComponents, diagramComponents), 'inSync');

  const shouldAutoBuild =
    enabled && isReady && changedThisSession && (status === 'stale' || status === 'untracked');
  const isOutOfSync =
    enabled &&
    !changedThisSession &&
    reactionSchemeFile.externalFile != null &&
    (status === 'stale' || status === 'missingVersions');

  const {build, isSyncing, isManuallySyncing} = useDiagramBuilds(reactionSchemeFile, onUpdate);

  useEffect(() => {
    if (!shouldAutoBuild) {
      return undefined;
    }
    return build(structuredTable, makeDiagramBuildRequest(structuredTable.rows, rows, diagramComponents))
      .cancel;
  }, [shouldAutoBuild, structuredTable, rows, diagramComponents, build]);

  // Holds the banner build's canceler so a superseding click or unmount discards a stale result.
  const cancelManualBuildRef = useRef<(() => void) | null>(null);
  useEffect(() => () => cancelManualBuildRef.current?.(), []);

  const syncNow = useCallback((): void => {
    if (!isOutOfSync || !isReady) {
      return;
    }
    if (status === 'missingVersions' && diagramComponents != null) {
      // The diagram already shows these components; only record their versions.
      onUpdate(
        reactionSchemeFile,
        reactUpdate(structuredTable, {
          metadata: {$merge: {syncedComponents: withRecordedVersions(tableComponents, diagramComponents)}},
        })
      );
      return;
    }
    cancelManualBuildRef.current?.();
    cancelManualBuildRef.current = build(
      structuredTable,
      makeDiagramBuildRequest(structuredTable.rows, rows, diagramComponents),
      {manual: true}
    ).cancel;
  }, [
    isOutOfSync,
    isReady,
    status,
    diagramComponents,
    tableComponents,
    rows,
    structuredTable,
    reactionSchemeFile,
    onUpdate,
    build,
  ]);

  return {isOutOfSync, isSyncing, isManuallySyncing, syncNow};
}

/**
 * Whether this session changed the reaction, which makes rebuilding the diagram automatically safe:
 * the change being drawn is the current user's own. Once true, it stays true. There are two signals,
 * each compared with its value when the Entry opened:
 *
 * - Saved identity: a reactant or product row's role or molecule changed in the saved cells. Saved
 *   cells only change through edits, unlike evaluated molecules, which resolve after the Entry opens.
 * - Versions: a molecule's version changed, as when a table Re-sync picks up an edit to the molecule.
 *   The saved Molecule Version can be out of date until it resolves, so each molecule's baseline is the
 *   first version that resolves after open. Only molecules the diagram recorded a version for count: a diagram built before version
 *   tracking can't be compared, so it keeps its banner.
 */
function useChangedThisSession(
  structuredTable: StoichiometryStructuredTable,
  isReady: boolean,
  tableComponents: ReactionComponents,
  diagramComponents: ReactionComponents | undefined
): boolean {
  const savedIdentity = useMemo(
    () => readSavedReactionIdentity(structuredTable.columns, structuredTable.rows),
    [structuredTable.columns, structuredTable.rows]
  );
  const openSavedIdentityRef = useRef(savedIdentity);
  const openVersionIdsRef = useRef<Map<string, string> | null>(null);
  const changedRef = useRef(false);

  if (!isEqual(savedIdentity, openSavedIdentityRef.current)) {
    changedRef.current = true;
  }

  if (isReady) {
    const versionIds = knownVersionIds(tableComponents);
    const openVersionIds = openVersionIdsRef.current;
    const recordedVersionIds = diagramComponents == null ? new Map() : knownVersionIds(diagramComponents);
    const isReVersioned = [...versionIds].some(
      ([moleculeId, versionId]) =>
        openVersionIds?.has(moleculeId) &&
        openVersionIds.get(moleculeId) !== versionId &&
        recordedVersionIds.has(moleculeId)
    );
    if (isReVersioned) {
      changedRef.current = true;
    }
    if (
      openVersionIds == null ||
      [...versionIds.keys()].some((moleculeId) => !openVersionIds.has(moleculeId))
    ) {
      // Existing baselines win; only newly resolved molecules are added.
      openVersionIdsRef.current = new Map([...versionIds, ...(openVersionIds ?? [])]);
    }
  }

  return changedRef.current;
}

/** The latest value from a render where `isReady` was true. */
function useSettledValue<T>(isReady: boolean, value: T, initialValue: T): T {
  const settledRef = useRef(initialValue);
  if (isReady) {
    settledRef.current = value;
  }
  return settledRef.current;
}

function serverNoteLinkedFileToClientNoteLinkedFile(
  serverNoteLinkedFile: FromServerNoteLinkedFile
): NoteLinkedFile {
  // Use the server-assigned id as the client cid so the patcher can find the
  // existing NoteLinkedFile on save without insecure creation via the patcher path.
  const {id, ...rest} = serverNoteLinkedFile;
  return {...rest, cid: id};
}

/** Runs diagram builds, saving each result unless it was canceled first. */
function useDiagramBuilds(
  reactionSchemeFile: NoteLinkedFile,
  onUpdate: OnUpdate
): {
  build: (
    structuredTable: StoichiometryStructuredTable,
    request: DiagramBuildRequest,
    options?: {manual?: boolean}
  ) => CancelablePromise<void>;
  isSyncing: boolean;
  isManuallySyncing: boolean;
} {
  // Counts rather than booleans, so overlapping runs (a canceled build that resolves after its
  // replacement started) can't clobber each other's spinner state.
  const [inFlightCount, setInFlightCount] = useState(0);
  const [manualInFlightCount, setManualInFlightCount] = useState(0);

  const build = useCallback(
    (
      structuredTable: StoichiometryStructuredTable,
      request: DiagramBuildRequest,
      options?: {manual?: boolean}
    ) => {
      const manual = options?.manual ?? false;
      const cancelable = makeCancelable(buildReactionFromComponents(request.reactants, request.products));
      setInFlightCount((count) => count + 1);
      if (manual) {
        setManualInFlightCount((count) => count + 1);
      }
      const run = async (): Promise<void> => {
        try {
          const {externalFile, noteLinkedFile} = await cancelable.promise;
          if (
            reactionSchemeFile.externalFile != null &&
            externalFile.id === reactionSchemeFile.externalFile.id
          ) {
            return;
          }
          onUpdate(
            serverNoteLinkedFileToClientNoteLinkedFile(noteLinkedFile),
            tableForBuiltDiagram(structuredTable, request)
          );
        } catch (err) {
          // A run superseded via cancel rejects with isCanceled; that's expected. Let any real
          // failure surface instead of silently leaving the diagram out of sync.
          if (!(err as {isCanceled?: boolean} | null)?.isCanceled) {
            throw err;
          }
        } finally {
          setInFlightCount((count) => count - 1);
          if (manual) {
            setManualInFlightCount((count) => count - 1);
          }
        }
      };
      return {promise: run(), cancel: cancelable.cancel};
    },
    [reactionSchemeFile, onUpdate]
  );

  return {build, isSyncing: inFlightCount > 0, isManuallySyncing: manualInFlightCount > 0};
}
